import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cluster } from "../../../models/Cluster";
import { useToasts } from "../../../contexts/toasts";
import { useFullScreenLoader } from "../../../contexts/fullScreenLoader";
import { Clusters } from "../../../api/Clusters";
import { Alert, Badge, Breadcrumb, Card, Col, Container, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import ClusterForm from "../../../components/forms/ClusterForm";
import { Flex, Tooltip } from "antd";
import { formatDate } from "../../../utils";
import ClusterNodeItem from "../../../components/list-items/ClusterNodeItem";
import UserItem from "../../../components/common/UserItem";
import AppLoader from "../../../components/common/AppLoader";


export default function ClusterDetail() {
  const id = window.__env.DEFAULT_CLUSTER_ID;
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [cluster, setCluster] = useState<Cluster>();
  const [error, setError] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const nodesFromDefinition = useMemo(() => {
    if (!cluster || !cluster.json) return [];
    return cluster.json.items?.filter((item: any) => item.kind === "Node") || [];
  }, [cluster]);

  const initCluster = useCallback(() => {
    Clusters.getCluster(+id!).then(
      ({ data }) => setCluster(data),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('cluster-error', message, `Cluster Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      }
    )
  }, [id, showToast]);

  const reset = useCallback(() => {
    setCluster(undefined);
    initCluster();
  }, [initCluster]);

  useEffect(() => initCluster(), [initCluster]);

  const onEditCluster = useCallback(() => setShowEditModal(true), []);
  const handleEditClose = useCallback(() => setShowEditModal(false), []);
  const handleNavigateToNotes = useCallback(() => navigate('notes'), [navigate]);

  const editCluster = useCallback((values: any) => {
    showLoader();
    const body = {
      ...values,
      json: typeof values.json === 'string' ? JSON.parse(values.json || "") : values.json,
    };

    Clusters.editCluster(cluster!.id!, body)
      .then(
        () => {
          hideLoader();
          showToast('cluster-success', `Cluster ${cluster ? 'updated' : 'created'} successfully`, 'Cluster Success', 'success');
          handleEditClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('cluster-error', message, `Cluster Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [cluster, showLoader, showToast, hideLoader, handleEditClose, reset]);

  const renderCluster = useCallback(() => {
    return (
      <Card className="item-detail-group main bg-cluster">
        <FontAwesomeIcon className="item-icon" icon={solid("circle-nodes")} />

        <div className="position-relative px-2 pt-2">
          <Flex justify="end">
            <Badge className="shadow">{cluster!.accessLevel}</Badge>
          </Flex>

          <div className="p-2">
            <Row className="gx-0 align-items-center justify-content-md-between">
              <Col xs={12} md="auto" className="pe-3">
                <h3 className="mb-0">{cluster!.name}</h3>
                <p className="text-trailing mb-3">{cluster!.description}</p>
              </Col>

              <Col xs={12} md="auto" className="text-center text-md-start">
                <Tooltip title="Edit Cluster">
                  <button className="circle-button" onClick={onEditCluster}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </button>
                </Tooltip>
                <Tooltip title="Cluster Notes">
                  <button className="circle-button" onClick={handleNavigateToNotes}>
                    <FontAwesomeIcon icon={solid('sticky-note')} />
                  </button>
                </Tooltip>
              </Col>
            </Row>
          </div>

          <Row className="gx-0 justify-content-between">
            <Col xs="auto">
              <div className="bg-highlight">
                <UserItem className="negative" user={cluster!.owner} organization={cluster!.organization} />
              </div>
            </Col>
          </Row>

          <div className="item-dates mb-2">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(cluster!.createDateTime!)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(cluster!.updateDateTime!)}</span>
            </div>
          </div>
        </div>
      </Card>
    )
  }, [cluster, onEditCluster, handleNavigateToNotes]);

  const handleNavigateToNode = useCallback((node: any) => () => navigate(`nodes/${node.metadata.uid}`), [navigate]);

  const renderClusterNodes = useCallback(() => {
    if (nodesFromDefinition.length === 0) return (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No nodes found.</span>
      </Alert>
    );

    return (
      <Row>
        {nodesFromDefinition.map((node: any) => (
          <Col key={node.metadata.uid} md={3} className="px-2 mb-3">
            <ClusterNodeItem item={node} onClick={handleNavigateToNode(node)} />
          </Col>
        )
        )}
      </Row>
    );
  }, [nodesFromDefinition, handleNavigateToNode]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during cluster retrieve. Try again later.</span>
      </Alert>
    );

    if (!cluster) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} active>Cluster Management</Breadcrumb.Item>
          {/* <Breadcrumb.Item active>{cluster.name}</Breadcrumb.Item> */}
        </Breadcrumb>

        {renderCluster()}

        <div className="d-flex flex-row my-2 align-items-center">
          <FontAwesomeIcon icon={solid('circle-nodes')} />
          <h4 className="ms-2 my-auto">Nodes</h4>
        </div>

        {renderClusterNodes()}
      </>
    )
  }, [error, cluster, renderCluster, renderClusterNodes]);

  return (
    <Container className="mt-3">
      {renderAsyncContent()}

      <Modal show={showEditModal} size="lg" onHide={handleEditClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Cluster</Modal.Title>
        </Modal.Header>

        <ClusterForm cluster={cluster} onCancel={handleEditClose} onSubmit={editCluster} />
      </Modal>
    </Container>
  )
}